import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router';

/** Compose rapid controls without losing queued URL changes; Back/Forward is authoritative. */
export function useSettingsUrlState() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const pending = useRef({ observedKey: location.key, params: new URLSearchParams(params), targets: new Set<string>() });
  useLayoutEffect(() => {
    if (pending.current.observedKey === location.key) return;
    pending.current.observedKey = location.key;
    const committed = params.toString();
    if (navigationType === 'POP' || !pending.current.targets.delete(committed)) {
      pending.current.params = new URLSearchParams(params);
      pending.current.targets.clear();
    } else if (committed === pending.current.params.toString()) pending.current.targets.clear();
  }, [location.key, navigationType, params]);
  const patch = (changes: Record<string, string | number | null>, resetPage = false) => {
    const next = new URLSearchParams(pending.current.params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '' || (key === 'page' && Number(value) === 1)) next.delete(key);
      else next.set(key, String(value));
    }
    if (resetPage) next.delete('page');
    pending.current.params = next;
    pending.current.targets.add(next.toString());
    setParams(next, { replace: false });
  };
  const page = Math.max(1, Math.min(1_000_000, Number.parseInt(params.get('page') ?? '1', 10) || 1));
  return { params, page, patch };
}
